/**
 * Isometric projection helpers — the single place world<->screen conversion
 * lives, so the rest of the renderer never hand-rolls iso math.
 *
 * NOTE ON THE SPEC: §4 rejects isometric by default because it implies a built
 * tile/furniture environment that proc-gen handles badly. The spec names ONE
 * exception: "reconsider isometric if the game becomes a habitat you arrange/
 * build." The Chao-Garden direction is exactly that habitat, so we take the
 * documented exception deliberately. Creatures remain front-facing flat SVG
 * billboards (no iso-projected redraw) to preserve the cloisonné look and the
 * magnet-export path; only the GROUND is isometric.
 *
 * Ground coordinates: (gx, gy) in "garden units", origin at garden center.
 * Standard 2:1 isometric (tile width = 2 * tile height).
 */

/** Half-width / half-height of a unit step on screen (2:1 iso). Larger = zoomed in. */
export const ISO = { tileW: 46, tileH: 23 };

export interface Point {
  x: number;
  y: number;
}

/** Garden ground (gx, gy) -> screen offset (before camera + viewport center). */
export function isoToScreen(gx: number, gy: number): Point {
  return {
    x: (gx - gy) * ISO.tileW,
    y: (gx + gy) * ISO.tileH,
  };
}

/** Screen offset -> garden ground (inverse of isoToScreen). */
export function screenToIso(sx: number, sy: number): Point {
  return {
    x: (sx / ISO.tileW + sy / ISO.tileH) / 2,
    y: (sy / ISO.tileH - sx / ISO.tileW) / 2,
  };
}

/**
 * Depth sort key: larger = nearer the viewer = drawn on top. In 2:1 iso, screen
 * Y increases with (gx + gy), so that sum is the natural depth order.
 */
export function depthKey(gx: number, gy: number): number {
  return gx + gy;
}

/*
 * DEPTH NUDGES — every offset the garden adds to `depthKey` so two things at the SAME ground point sort in a
 * fixed order. They are one scale: change one only with the others in view, because the order below is the
 * whole contract. At equal gx+gy, back → front:
 *
 *   DEPTH_EPS_INSECT_TUCK    −0.01   a flier behind a decor item's canopy, tucked just behind that item
 *   (0)                              decor, ground plants, fence halves — the plain depthKey
 *   DEPTH_EPS_POST_OWNER     +1e-4   the fence half that OWNS a lattice post (sim/fence.ts postOwner), so it
 *                                    draws over every other half that meets it there — two halves at a
 *                                    corner or tee sit at exactly the same gx+gy and would otherwise tie
 *                                    (half of it orders the two non-owner halves at a tee; fenceHalfDepth)
 *   DEPTH_EPS_FRONT_TUFT     +0.001  grass tufts lifted in front of a ground plant's foot
 *   DEPTH_EPS_HOSTED_PLANT   +0.02   a plant in a planter/pot, just above its host
 *   DEPTH_EPS_GHOST          +0.05   the plant placement ghost, above whatever it is hovering over
 *   DEPTH_EPS_INSECT_RESTING +0.5    a landed/crawling insect, on top of the blossom/patch it sits on
 *   DEPTH_INSECT_AIRBORNE    +1e5    a flying insect, in front of every short object at any depth (not an
 *                                    epsilon — a float-to-front that only a tall item's canopy overrides,
 *                                    via the tuck), so a flier never hides behind something low it is above
 *
 * The ones under 0.1 only break ties at (near-)equal depth. The resting insect's 0.5 is deliberately wider,
 * so a bug on a blossom is not cut by its own plant's neighbours, yet anything clearly nearer (> 0.5 in
 * gx+gy) still covers it. The decor placement ghost uses no nudge — it sorts exactly as the item it previews.
 */
export const DEPTH_EPS_INSECT_TUCK = -0.01;
export const DEPTH_EPS_POST_OWNER = 0.0001;   // keep below every other nudge: it only breaks a fence's own tie
export const DEPTH_EPS_FRONT_TUFT = 0.001;
export const DEPTH_EPS_HOSTED_PLANT = 0.02;
export const DEPTH_EPS_GHOST = 0.05;
export const DEPTH_EPS_INSECT_RESTING = 0.5;
export const DEPTH_INSECT_AIRBORNE = 100000;
